import React, { useCallback, useEffect, useRef, useState } from "react";
import { Drawer, Skeleton } from "antd";
import dayjs from "dayjs";
import CgIcon from "./CgIcon";
import { formatRupees } from "./money";
import { getPharmacyAuditLogsApi } from "../../../services/pharmacyAuditLogs.services";
import styles from "./auditLog.module.css";


export const AUDIT_ENTITIES = {
  order: "pharmacy_order",
  purchaseEntry: "purchase_entry",
  purchaseOrder: "purchase_order",
  purchaseReturn: "purchase_return",
};

const PAGE_SIZE = 50;


const EVENTS = {
  created: { title: "Created", icon: "plus", tone: "add" },
  item_added: { title: "Product added", icon: "plus", tone: "add" },
  added: { title: "Product added", icon: "plus", tone: "add" },
  item_updated: { title: "Product changed", icon: "edit", tone: "edit" },
  item_removed: { title: "Product removed", icon: "trash", tone: "undo" },
  removed: { title: "Product removed", icon: "trash", tone: "undo" },
  updated: { title: "Edited", icon: "edit", tone: "edit" },
  edited: { title: "Edited", icon: "edit", tone: "edit" },
  submitted: { title: "Submitted", icon: "check", tone: "done" },
  completed: { title: "Completed", icon: "check", tone: "done" },
  cancelled: { title: "Cancelled", icon: "close", tone: "undo" },
  deleted: { title: "Deleted", icon: "trash", tone: "undo" },
};

const pretty = (value) =>
  String(value || "")
    .split("_")
    .join(" ")
    .replace(/^./, (c) => c.toUpperCase());

const eventLook = (action) =>
  EVENTS[String(action).toLowerCase()] || {
    title: pretty(action) || "Changed",
    icon: "clock",
    tone: "plain",
  };

/* ---- reading a value the way it is spoken ------------------------------- */

const ISO_DATETIME = /^\d{4}-\d{2}-\d{2}T/;
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

/* Fields that hold money, so they are read with the rupee on them. */
const MONEY_FIELDS = new Set([
  "mrp",
  "amount",
  "discount",
  "rate",
  "rate_a",
  "rate_b",
  "rate_c",
  "purchase_rate",
  "override_rate",
  "loose_price",
  "selling_price",
  "taxable_amount",
  "subtotal",
  "total_amount",
  "grand_total",
  "total",
  "gst_amount",
  "sgst",
  "cgst",
  "igst",
  "cess",
]);

const number = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const readable = (field, value) => {
  if (value === null || value === undefined || value === "") return "nothing";
  if (typeof value === "boolean") return value ? "yes" : "no";

  if (MONEY_FIELDS.has(field)) {
    const amount = number(value);
    if (amount !== null) return formatRupees(amount);
  }

  const text = String(value);
  if (ISO_DATETIME.test(text)) return dayjs(text).format("DD MMM YYYY, hh:mm A");
  if (ISO_DATE.test(text)) return dayjs(text).format("DD MMM YYYY");
  if (typeof value === "number") return String(value);
  return pretty(text);
};

const units = (qty) => `${qty} ${Math.abs(qty) === 1 ? "unit" : "units"}`;

/*
 * A line's name, without the batch the server appends to it - the batch is
 * said separately, and "TEST MEDICINE / 87, batch 87" reads as a stammer.
 */
const medicineName = (label) => String(label || "").split("/")[0].trim() || "A product";
const batchFrom = (label) => {
  const parts = String(label || "").split("/");
  return parts.length > 1 ? parts.slice(1).join("/").trim() : null;
};

/*
 * Bookkeeping the event already says. `cancelled_at` on a cancellation, and
 * the row's own timestamps, only repeat the line above them.
 */
const NOISE_FIELDS = new Set([
  "cancelled_at",
  "submitted_at",
  "updated_at",
  "created_at",
  "editable_until",
  "modified_at",
]);

/* ---- what one event did, in a sentence or two --------------------------- */

const FieldChange = ({ field, move }) => (
  <li>
    <b>{pretty(field)}</b> changed from <em>{readable(field, move?.old)}</em> to{" "}
    <b>{readable(field, move?.new)}</b>
  </li>
);

/*
 * What a line says about itself: how many, on which batch, at what price.
 *
 * One fact per line, each named. They used to be joined into a single
 * sentence - "100 units · batch 0909 · expires 30 Nov 2026 · at ₹85.00 each"
 * - which wraps into a paragraph and has to be read through to find the one
 * figure anybody came looking for.
 *
 * Only the figures that carry something: a rate of nothing and a discount of
 * nothing are not facts about this line, they are empty columns.
 */
const lineFacts = (line) => {
  const values = line?.values || {};
  const facts = [];

  const qty = number(values.qty);
  if (qty !== null && qty !== 0) facts.push(["Quantity", units(qty)]);

  const free = number(values.free_qty);
  if (free) facts.push(["Free", units(free)]);

  const batch = values.batch_number || batchFrom(line?.label);
  if (batch) facts.push(["Batch", batch]);

  if (values.expiry) facts.push(["Expiry", readable("expiry", values.expiry)]);

  const rate = number(values.purchase_rate ?? values.rate);
  if (rate) facts.push(["Rate", formatRupees(rate)]);

  const mrp = number(values.mrp);
  if (mrp) facts.push(["MRP", formatRupees(mrp)]);

  const discount = number(values.discount);
  if (discount) facts.push(["Discount", formatRupees(discount)]);

  const amount = number(values.amount);
  if (amount) facts.push(["Amount", formatRupees(amount)]);

  return facts;
};

/*
 * What a changed line moved.
 *
 * An added or removed line describes itself in `values` - how many, which
 * batch, at what price. A line that was *changed* carries something else: its
 * own `changes` map, a field at a time, exactly as the document's own fields
 * arrive. `values` was all that was read, so "Aarcid Syrup changed" appeared
 * with nothing after it - true, and no use to anyone asking what changed.
 */
const lineMoves = (line) =>
  Object.entries(line?.changes || {}).filter(
    ([field, move]) => !NOISE_FIELDS.has(field) && isMove(move)
  );

const LINE_VERBS = {
  added: "added",
  updated: "changed",
  removed: "taken off",
};

/* The three shapes this panel reads outright; anything else is a field. */
const STRUCTURED_KEYS = new Set(["fields", "items", "stock"]);

/** A before-and-after pair, however thinly the server spells it. */
const isMove = (value) =>
  Boolean(value) &&
  typeof value === "object" &&
  !Array.isArray(value) &&
  ("old" in value || "new" in value);

/*
 * A group of pairs under one heading.
 *
 * The server sometimes nests the pairs a level down instead of listing them
 * flat - the bill an order was rebuilt into arrives as `{"bill": {"subtotal":
 * {...}, "grand_total": {...}}}`. The pairs inside are ordinary pairs; only
 * the wrapper is new, and without being told about it the panel printed the
 * whole wrapper as JSON under the heading it had already written.
 */
const isMoveGroup = (value) =>
  Boolean(value) &&
  typeof value === "object" &&
  !Array.isArray(value) &&
  !isMove(value) &&
  Object.values(value).length > 0 &&
  Object.values(value).every(isMove);

/*
 * The fields that moved.
 *
 * They arrive under `fields` where the server groups them, and as the body of
 * `changes` itself where it does not - `{"rate": {"old": 1, "new": 2}}` with
 * nothing wrapped around it. Only the first was read before, so an event
 * recorded the second way showed its heading and date with nothing under it,
 * which reads as "nothing changed" rather than "not understood".
 */
const movedFields = (changes) => {
  const grouped =
    changes.fields && typeof changes.fields === "object" && !Array.isArray(changes.fields)
      ? changes.fields
      : null;

  return Object.entries(grouped || changes).filter(
    ([field, move]) =>
      !NOISE_FIELDS.has(field) &&
      (grouped ? true : !STRUCTURED_KEYS.has(field)) &&
      isMove(move)
  );
};

/*
 * Whatever is left over.
 *
 * Not a field pair, not lines, not stock - a shape nobody has taught this
 * panel yet. It is printed as it came rather than dropped: an audit log that
 * quietly leaves things out is worse than one that is occasionally ugly.
 */
const leftovers = (changes) =>
  Object.entries(changes).filter(
    ([key, value]) =>
      !STRUCTURED_KEYS.has(key) &&
      !NOISE_FIELDS.has(key) &&
      !isMove(value) &&
      value !== null &&
      value !== undefined &&
      value !== ""
  );

const Leftover = ({ field, value }) => {
  const plain = typeof value !== "object";
  /* A nest of pairs is read as pairs, under the heading it came with. */
  const group = isMoveGroup(value) ? Object.entries(value) : null;

  return (
    <li>
      <b>{pretty(field)}</b>
      {plain ? (
        <>
          {" "}set to <b>{readable(field, value)}</b>
        </>
      ) : group ? (
        <ul className={styles.fields}>
          {group.map(([key, move]) => (
            <FieldChange key={key} field={key} move={move} />
          ))}
        </ul>
      ) : (
        <div className={styles.raw}>{JSON.stringify(value)}</div>
      )}
    </li>
  );
};

const Changes = ({ changes }) => {
  if (!changes || typeof changes !== "object") return null;

  const fields = movedFields(changes);
  const items = changes.items || {};
  const stock = Array.isArray(changes.stock) ? changes.stock : [];
  const rest = leftovers(changes);

  const lineGroups = ["added", "updated", "removed"].filter(
    (key) => Array.isArray(items[key]) && items[key].length
  );

  if (!fields.length && !lineGroups.length && !stock.length && !rest.length) {
    return null;
  }

  return (
    <div className={styles.changes}>
      {fields.length ? (
        <ul className={styles.fields}>
          {fields.map(([field, move]) => (
            <FieldChange key={field} field={field} move={move} />
          ))}
        </ul>
      ) : null}

      {rest.length ? (
        <ul className={styles.fields}>
          {rest.map(([field, value]) => (
            <Leftover key={field} field={field} value={value} />
          ))}
        </ul>
      ) : null}

      {lineGroups.map((key) =>
        items[key].map((line, index) => {
          const moves = lineMoves(line);
          /*
           * A changed line says what changed; an added or removed one says
           * what it was.
           *
           * Printing the line's whole state under "changed" as well made an
           * edit read exactly like a removal - the same eight figures under
           * both, with nothing to say which of them had actually moved.
           */
          const facts = key === "updated" && moves.length ? [] : lineFacts(line);
          return (
            <div className={styles.line} key={`${key}-${line?.id ?? index}`}>
              <span className={styles.lineName}>{medicineName(line?.label)}</span>{" "}
              <span className={styles.lineVerb}>{LINE_VERBS[key]}</span>
              {facts.length ? (
                <ul className={`${styles.fields} ${styles.lineFields}`}>
                  {facts.map(([label, value]) => (
                    <li key={label}>
                      <b>{label}</b> {value}
                    </li>
                  ))}
                </ul>
              ) : null}
              {moves.length ? (
                <ul className={`${styles.fields} ${styles.lineFields}`}>
                  {moves.map(([field, move]) => (
                    <FieldChange key={field} field={field} move={move} />
                  ))}
                </ul>
              ) : null}
            </div>
          );
        })
      )}

      {stock.map((move, index) => {
        const qty = number(move?.qty) || 0;
        const batch = batchFrom(move?.label);
        return (
          <div className={styles.stock} key={move?.id ?? index}>
            <span className={qty < 0 ? styles.out : styles.in}>
              {units(Math.abs(qty))}
            </span>{" "}
            of <b>{medicineName(move?.label)}</b>{" "}
            {qty < 0 ? "taken out of stock" : "put into stock"}
            {batch ? ` · batch ${batch}` : ""}
          </div>
        );
      })}
    </div>
  );
};

const AuditLogDrawer = ({ open, onClose, entityType, entityId, documentNumber }) => {
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [morePages, setMorePages] = useState(false);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);

  /*
   * The document whose log is on screen, and the request in flight for it.
   *
   * `live` drops an answer that arrives after the drawer has moved on, so a
   * slow first page cannot land on top of a second document's history.
   */
  const liveRef = useRef(null);

  const load = useCallback(
    (nextPage) => {
      const ticket = {};
      liveRef.current = ticket;
      setLoading(true);
      setFailed(false);

      getPharmacyAuditLogsApi(
        new URLSearchParams({
          entity_type: entityType,
          entity_id: entityId,
          page: nextPage,
          limit: PAGE_SIZE,
        })
      )
        .then((res) => {
          if (liveRef.current !== ticket) return;
          const data = res?.data;
          const list = data?.results || (Array.isArray(data) ? data : []);
          /* A later page is added to what is already read rather than
             replacing it - the rail is one history, not one page of one. */
          setRows((prev) => (nextPage === 1 ? list : [...prev, ...list]));
          setMorePages(Boolean(data?.next_page));
          setPage(nextPage);
        })
        .catch(() => {
          if (liveRef.current !== ticket) return;
          if (nextPage === 1) setRows([]);
          setFailed(true);
        })
        .finally(() => {
          if (liveRef.current === ticket) setLoading(false);
        });
    },
    [entityType, entityId]
  );

  /*
   * One read per opening.
   *
   * The effect watches four things, and three of them can move without the
   * question changing: `load` is rebuilt whenever the entity does, and the
   * entity itself is read off an order that is refetched as the screen is
   * used. Every one of those re-ran the effect and asked the server for the
   * same history again - and in development React runs a mount effect twice
   * over on top of that, which is how one click came to make three requests.
   *
   * So what has already been read is remembered, and the log is asked for
   * only when the drawer is opened on a document it has not read yet.
   * Closing forgets it, so reopening picks up anything recorded since.
   */
  const readFor = useRef(null);

  useEffect(() => {
    if (!open) {
      readFor.current = null;
      return;
    }
    if (!entityType || !entityId) return;

    const key = `${entityType}:${entityId}`;
    if (readFor.current === key) return;
    readFor.current = key;

    setRows([]);
    setMorePages(false);
    load(1);
  }, [open, entityType, entityId, load]);

  /* Newest first - what happened last is what anyone opening this came for. */
  const events = [...rows].sort(
    (a, b) => dayjs(b?.created_at).valueOf() - dayjs(a?.created_at).valueOf()
  );

  return (
    <Drawer
      className={styles.drawer}
      title={
        <div className={styles.head}>
          <span className={styles.headTitle}>Audit log</span>
          {documentNumber ? (
            <span className={styles.headSub}>{documentNumber}</span>
          ) : null}
        </div>
      }
      placement="right"
      width={440}
      open={open}
      onClose={onClose}
      destroyOnClose
    >
      {loading && !events.length ? (
        <Skeleton active paragraph={{ rows: 6 }} />
      ) : failed && !events.length ? (
        <div className={styles.empty}>
          <CgIcon name="alert" size={22} stroke={1.9} />
          <span>The audit log could not be loaded.</span>
        </div>
      ) : events.length ? (
        <>
          <ol className={styles.rail}>
            {events.map((event) => {
              const look = eventLook(event?.action);
              return (
                <li className={styles.event} key={event?.id}>
                  <span className={`${styles.mark} ${styles[look.tone]}`}>
                    <CgIcon name={look.icon} size={13} stroke={2.4} />
                  </span>

                  <div className={styles.body}>
                    <div className={styles.what}>
                      {look.title}
                      {/* Anything done after the document was submitted is
                          worth marking: it changed a record, not a draft. */}
                      {event?.after_submission ? (
                        <span className={styles.late}>after submission</span>
                      ) : null}
                    </div>

                    <div className={styles.when}>
                      {event?.performed_by_name
                        ? `by ${event.performed_by_name} · `
                        : ""}
                      {event?.created_at
                        ? dayjs(event.created_at).format("DD MMM YYYY, hh:mm A")
                        : "—"}
                    </div>

                    {event?.reason ? (
                      <div className={styles.reason}>“{event.reason}”</div>
                    ) : null}

                    <Changes changes={event?.changes} />
                  </div>
                </li>
              );
            })}
          </ol>

          {morePages ? (
            <button
              type="button"
              className={styles.more}
              disabled={loading}
              onClick={() => load(page + 1)}
            >
              {loading ? "Loading…" : "Show earlier events"}
            </button>
          ) : null}
        </>
      ) : (
        <div className={styles.empty}>
          <CgIcon name="clock" size={22} stroke={1.9} />
          <span>Nothing has been recorded against this yet.</span>
        </div>
      )}
    </Drawer>
  );
};

export default AuditLogDrawer;
